// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/GuestChatDrawer.tsx
// 🔧 PURPOSE: La chat dell'ospite con la reception, a tutto schermo, con la
//             traduzione automatica dei messaggi dello staff.
//             Mockup approvati "Chat · conversazione" e "Chat · stati"
//             (testata BLU): testata con "‹", stemma e titolo in Fraunces,
//             fondo osso, bolle di sempre (blu l'ospite, verdi lo staff), card
//             d'ordine e di consegna della famiglia, risposte rapide con le
//             icone, barra in basso come quella dei servizi.
//             Stati: caricamento (bolle segnaposto), vuota, errore con
//             RIPROVA, offline (i messaggi aspettano in coda e partono da
//             soli), sessione scaduta (la chat resta aperta con "Accedi di
//             nuovo"), tastiera aperta (la chat segue il visualViewport:
//             su iPhone la testata resta in vista).
// ============================================

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Sheet, SheetContent } from './ui/sheet';
import {
  Mail,
  Send,
  Loader2,
  AlertCircle,
  X,
  Globe,
  Check,
  CheckCheck,
  Clock,
  ArrowDown,
  RotateCw,
  Camera,
  Info,
  MessageCircle,
  Wifi,
  Trash2,
  Heart,
  Coffee,
  Wine,
  GlassWater,
  Sparkles,
  Bath,
  Toilet,
  ShowerHead,
  Snowflake,
  WashingMachine,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { appLanguageOf, intlLocale } from '../lib/locale';
import { dayKeyDiff, romeDayKey, romeHour, ROME_TZ, shiftDayKey } from '../lib/romeDay';
import { storedCheckInState } from '../hooks/useGuestSession';
import {
  AppExtra,
  ChatMessage,
  ChatAttachment,
  ExtrasCatalog,
  MessageReaction,
  CHAT_PHOTO_ONLY_TEXT,
  CHAT_PHOTO_MAX_BYTES,
  fetchChatMessages,
  sendChatMessage,
  markMessagesAsRead,
  sendReaction,
  uploadChatPhoto,
  chatPhotoErrorKey,
  isChatPhotoErrorRetryable,
  fetchBookingExtras,
  fetchBookingInfo,
} from '../lib/guestApi';
import { compressImage, isHeic } from '../lib/imageCompress';
import MessageReactions from './MessageReactions';
import {
  ChatPhotoGrid,
  ChatPhotoViewer,
  PendingPhoto,
  PendingPhotoStrip,
  ViewerPhoto,
  isPhotoOnlyText,
  preloadChatThumbs,
} from './ChatPhotos';
import DeliveryCard, { OrderCard } from './DeliveryCard';
import NonRefundableBadge from './NonRefundableBadge';
import ChatLinkify from './ChatLinkify';
import ChatHeader, { CHAT_HEADER_COLOR, CHAT_HEADER_DARK_COLOR } from './chat/ChatHeader';
import {
  ChatEmptyWelcome,
  ChatLoadError,
  ChatLoadingSkeleton,
  ChatOfflineBanner,
  ChatSessionExpired,
  ConnectedAs,
} from './chat/ChatStates';
import ChatRelogin from './chat/ChatRelogin';
import {
  OutboxItem,
  enqueueOutbox,
  outboxFor,
  outboxOwner,
  subscribeOutbox,
  takeFromOutbox,
} from './chat/chatOutbox';
import { isNavigatorOnline, useKeyboardViewport, useOnlineStatus } from './chat/useChatViewport';
import { useStatusBarOverlay } from '../lib/statusBar';
import { cn } from '../lib/utils';
import {
  TranslationLangCode,
  getInitialTranslationLang,
  saveTranslationLang,
  translateMessageFields,
  TranslatedMessageFields,
} from '../lib/translationService';

interface GuestChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
  guestName: string | null;
  /**
   * Il server dice che la sessione è scaduta. La chat NON si chiude: resta
   * aperta con la card "Accedi di nuovo". Si chiama solo insieme a
   * `onRelogin` (il genitore ha il suo login: la Home esce dalla sessione ma
   * tiene aperta la chat); senza `onRelogin` fa tutto la chat.
   */
  onSessionExpired?: () => void;
  /**
   * "Accedi di nuovo": il genitore apre il suo login (precompilato) e dopo
   * l'accesso torna qui. Senza, la chat apre il login di sempre sopra di sé
   * (ChatRelogin) con i dati della sessione scaduta.
   */
  onRelogin?: () => void;
  isNonRefundable?: boolean;
  /**
   * Testo da mettere nella casella all'apertura (es. "Ciao, avete X?" dalla
   * ricerca degli Extra). Si scrive e basta: l'ospite lo manda, lo cambia o
   * lo cancella. Se stava già scrivendo qualcosa, vince il suo testo.
   */
  initialDraft?: string | null;
}

// Polling adattivo. Con 30s fissi una risposta dello staff arrivava tardi
// proprio mentre l'ospite aspettava, e a chat ferma si interrogava il server
// per niente. Ora: 5s nei 2 minuti dopo che l'ospite scrive o riceve (la
// conversazione e' viva), 20s fino a 10 minuti (il tempo in cui di solito
// rispondiamo), poi 60s. Con l'app nascosta non si interroga affatto.
const POLL_FAST_MS = 5000;
const POLL_MEDIUM_MS = 20000;
const POLL_SLOW_MS = 60000;
const FAST_WINDOW_MS = 2 * 60 * 1000;
const MEDIUM_WINDOW_MS = 10 * 60 * 1000;

function pollDelay(lastActivity: number): number {
  const since = Date.now() - lastActivity;
  if (since < FAST_WINDOW_MS) return POLL_FAST_MS;
  if (since < MEDIUM_WINDOW_MS) return POLL_MEDIUM_MS;
  return POLL_SLOW_MS;
}

// Sotto questa distanza dal fondo la lista "resta attaccata" agli ultimi
// messaggi: quelli nuovi la fanno scorrere. Piu' su l'ospite sta rileggendo
// e non lo si sposta: compare la pillola "Nuovi messaggi".
const STICK_THRESHOLD_PX = 80;

// La casella cresce fino a 5 righe (24px l'una + 21px di padding + 3px di
// bordo), poi scorre. A una riga e' alta 48px come il pulsante di invio.
const COMPOSER_MAX_HEIGHT_PX = 5 * 24 + 21 + 3;

// Foto per messaggio: oltre, la striscia sopra la casella diventa
// ingestibile e allo staff arriva un album invece di una domanda.
const MAX_PHOTOS_PER_MESSAGE = 6;
const PHOTO_MAX_SIDE = 1600;
const PHOTO_QUALITY = 0.82;

/** Il nome del file da caricare: se l'app l'ha ricompresso ora e' un JPEG. */
function uploadFilename(file: File, blob: Blob): string {
  const name = file.name || 'photo';
  if (blob === file) return name;
  return `${name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`;
}

// Giorni e orari sempre nel fuso degli appartamenti: il separatore "Oggi" e
// l'orario nella bolla devono dire la stessa cosa della card di consegna.
// Gli helper stanno in src/lib/romeDay.ts (li usa anche MyStay).
const TZ = ROME_TZ;

function capitalizeFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// ---------- Risposte rapide "intelligenti" ----------

// Il catalogo degli extra serve alle risposte rapide: si chiede quando la
// chat si apre e vale 5 minuti (per prenotazione e lingua), cosi' aprire e
// chiudere la chat non rifa' la richiesta ogni volta. I drawer della Home e
// del Soggiorno condividono la stessa copia.
const QUICK_CATALOG_TTL_MS = 5 * 60 * 1000;
let quickCatalogCache: { key: string; at: number; catalog: ExtrasCatalog } | null = null;
const quickCatalogKey = (token: string, lang: string) => `${token}|${appLanguageOf(lang)}`;

/** La sera (16:00-23:59, ora di Roma) si propone la colazione di domani. */
const EVENING_FROM_HOUR = 16;
// Una sola proposta di scorte alla volta: asciugamani prima di tutto.
const SUPPLY_SERVICE_IDS = ['linen_supplies', 'paper_supplies', 'hygiene_supplies'];
// Oltre a Wi-Fi e Rifiuti, al massimo due proposte: la fila resta di 4.
const MAX_OFFER_CHIPS = 2;
// Di giorno, in soggiorno, il vino (o l'acqua) si propone circa 3 aperture
// della chat su 4: abbastanza per vendere, non tanto da diventare un muro.
const DAYTIME_DRINK_ODDS = 0.75;
const MAX_QUICK_CHIPS = 4;

interface StayDates {
  token: string;
  checkIn: string;
  checkOut: string;
  /** Check-in online fatto secondo il token (può essere rimasto indietro). */
  checkedIn: boolean;
}

/** Quello che serve alle proposte oltre al catalogo e alle date. */
interface OfferContext {
  /** Check-in online fatto: prima dell'arrivo il vino si propone solo così. */
  checkInDone: boolean;
  /** L'estrazione di QUESTA apertura della chat: il vino di giorno, sì o no. */
  daytimeDrink: boolean;
}

/** Il servizio in catalogo, acquistabile adesso e non gia' preso (null altrimenti). */
function buyableNow(catalog: ExtrasCatalog, serviceId: string): AppExtra | null {
  const item = catalog.items.filter((i) => i.serviceId === serviceId)[0];
  if (!item || !item.available || item.alreadyPurchased) return null;
  if (catalog.purchasedExtras.some((e) => e.serviceId === serviceId)) return null;
  return item;
}

function lateCheckoutBought(catalog: ExtrasCatalog): boolean {
  return catalog.purchasedExtras.some((e) => e.serviceId === 'late_checkout') || !!catalog.lateCheckout?.purchasedAt;
}

/** Il late check-out si compra adesso: l'offerta e' maturata e non e' gia' preso. */
function lateCheckoutOpen(catalog: ExtrasCatalog): boolean {
  if (lateCheckoutBought(catalog)) return false;
  const offer = catalog.lateCheckout;
  if (offer) return offer.status === 'disponibile';
  return !!buyableNow(catalog, 'late_checkout');
}

interface OfferPick {
  serviceId: string;
  item: AppExtra | null;
  score: number;
  /**
   * Late check-out "in attesa": non si compra ancora, ma potrebbe diventarlo
   * (e allora arriva la push). Il chip c'e' e al tocco lo dice, invece di
   * aprire un negozio che non lo vende.
   */
  pending?: boolean;
}

interface QuickReply {
  key: string;
  label: string;
  /** L'icona del chip (al posto dell'emoji di prima). */
  icon: React.ReactNode;
  onClick: () => void;
  /** Una proposta del negozio (colore diverso dalle risposte di servizio). */
  offer?: boolean;
}

/**
 * Le proposte giuste per QUESTO momento del soggiorno, dalla piu'
 * pertinente. Si propone solo cio' che il catalogo dice acquistabile adesso
 * e non ancora preso: se e quando si puo' comprare lo decide il server, qui
 * si sceglie soltanto cosa mettere sotto il pollice. Giorni e ore di Roma,
 * come il catalogo.
 */
function pickOfferChips(catalog: ExtrasCatalog, dates: StayDates | null, now: Date, ctx: OfferContext): OfferPick[] {
  const today = romeDayKey(now);
  const evening = romeHour(now) >= EVENING_FROM_HOUR;
  const inKey = dates ? romeDayKey(dates.checkIn) : '';
  const outKey = dates ? romeDayKey(dates.checkOut) : '';
  const phase = catalog.stayPhase;
  const isCheckoutDay = phase === 'checkout_day' || (!!outKey && today === outKey);
  // "Ultimo giorno": la sera prima della partenza e il giorno stesso.
  const isLastDay = isCheckoutDay || (!!outKey && today === shiftDayKey(outKey, -1));
  const isArrivalDay = !!inKey && today === inKey;
  const inStay = phase
    ? phase === 'in_stay' || phase === 'checkout_day'
    : !!inKey && !!outKey && today >= inKey && today <= outKey;
  const beforeArrival = phase ? phase === 'before_checkin' : !!inKey && today < inKey;
  const nights = inKey && outKey ? dayKeyDiff(outKey, inKey) : null;
  // 0 = il giorno dell'arrivo, 1 = il secondo giorno...
  const dayOfStay = inKey ? dayKeyDiff(today, inKey) : null;

  const picks: OfferPick[] = [];
  const offer = (serviceId: string, score: number): boolean => {
    const item = buyableNow(catalog, serviceId);
    if (!item) return false;
    picks.push({ serviceId, item, score });
    return true;
  };

  // L'ultimo giorno uscire un'ora dopo e' la cosa che serve adesso: si
  // propone se si compra, o se l'offerta e' "in attesa" (potrebbe maturare).
  // Non disponibile o gia' preso: niente chip.
  if (isLastDay && !lateCheckoutBought(catalog)) {
    const lateItem = catalog.items.filter((i) => i.serviceId === 'late_checkout')[0] || null;
    if (lateCheckoutOpen(catalog)) {
      picks.push({ serviceId: 'late_checkout', item: lateItem, score: 100 });
    } else if (catalog.lateCheckout && catalog.lateCheckout.status === 'in_attesa') {
      picks.push({ serviceId: 'late_checkout', item: lateItem, score: 100, pending: true });
    }
  }
  // La sera: la colazione per domani mattina (non la sera della partenza).
  if (evening && inStay && !isCheckoutDay) offer('caffe_brioche', 90);
  // Da bere: Vino & Prosecco, oppure l'acqua quando il vino è già stato
  // preso in questo soggiorno o non si vende (buyableNow guarda anche gli
  // acquisti del catalogo). Stesse regole per entrambi, mai il giorno della
  // partenza:
  // - il giorno dell'arrivo e tutte le sere in soggiorno: sempre, subito
  //   sotto il late check-out (sopra la colazione di domani);
  // - di giorno in soggiorno: quando l'estrazione di questa apertura dice sì;
  // - prima dell'arrivo: solo a check-in online fatto.
  if (!isCheckoutDay) {
    const drinkId = buyableNow(catalog, 'welcome_wine') ? 'welcome_wine' : 'water_bottles';
    if (isArrivalDay || (evening && inStay)) offer(drinkId, 95);
    else if (inStay) { if (ctx.daytimeDrink) offer(drinkId, 80); }
    else if (beforeArrival && ctx.checkInDone) offer(drinkId, 80);
  }
  // Pulizia a meta' soggiorno: solo dalle 3 notti in su, mai l'ultimo giorno.
  if (inStay && !isLastDay && nights !== null && nights >= 3) offer('extra_cleaning', 70);
  // Scorte: dal secondo giorno, quando qualcosa finisce davvero. Una sola.
  if (inStay && !isCheckoutDay && dayOfStay !== null && dayOfStay >= 1) {
    for (let i = 0; i < SUPPLY_SERVICE_IDS.length; i++) {
      if (offer(SUPPLY_SERVICE_IDS[i], 55)) break;
    }
  }

  picks.sort((a, b) => b.score - a.score);
  return picks.slice(0, MAX_OFFER_CHIPS);
}

// ---------- Traduzione delle card ----------

const isCardKind = (kind?: string): boolean => kind === 'delivery' || kind === 'delivered' || kind === 'order';

/**
 * La lingua in cui e' scritta una card: quella che manda il server, se la
 * manda; altrimenti quella dell'app (il server scrive le card nella lingua
 * dell'ospite, che e' quella dell'app).
 */
function cardLanguageOf(msg: ChatMessage, appLang: string): string {
  const metaLocale = msg.meta ? msg.meta.locale : undefined;
  const raw = typeof msg.locale === 'string' && msg.locale
    ? msg.locale
    : typeof metaLocale === 'string' && metaLocale ? metaLocale : '';
  return raw ? raw.toLowerCase().split('-')[0] : appLang;
}

/** La nota dello staff di una card di consegna ('' se non c'e'). */
function cardNoteOf(msg: ChatMessage): string {
  const note = msg.meta ? msg.meta.note : undefined;
  return typeof note === 'string' ? note.trim() : '';
}

/** Sul telefono Invio va a capo e si spedisce col pulsante; con mouse e tastiera Invio spedisce. */
function hasFinePointer(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: fine)').matches;
}

/**
 * Stato locale del messaggio dell'ospite, prima che il server lo confermi:
 *  - 'queued'  = scritto senza rete: aspetta nella coda (chatOutbox) e
 *                parte da solo quando la rete torna (orologio);
 *  - 'sending' = appena scritto, la POST e' in corso (orologio);
 *  - 'sent'    = la POST ha risposto, la lista del server non l'ha ancora (✓);
 *  - 'failed'  = la POST e' fallita: "Tocca per riprovare".
 * I messaggi letti dal server non hanno stato: sono nella chat dello staff (✓✓).
 */
type LocalStatus = 'queued' | 'sending' | 'sent' | 'failed';

interface UiMessage extends ChatMessage {
  status?: LocalStatus;
  /**
   * Le foto appena scelte (object URL), allineate a `attachments`: la bolla
   * temporanea le mostra subito, senza aspettare le miniature del server.
   * Spariscono quando arriva il messaggio vero dalla lista.
   */
  localThumbs?: string[];
}

const TEMP_ID_PREFIX = 'tmp-';
const isTempId = (id: string) => id.indexOf(TEMP_ID_PREFIX) === 0;

/**
 * La lista del server piu' i messaggi locali che il server non ha ancora:
 * quelli in invio e quelli falliti (altrimenti il polling li cancellerebbe
 * sotto gli occhi dell'ospite), e quelli "inviati" la cui GET era partita
 * prima che la POST finisse. Un temporaneo confermato senza id vero (POST
 * che non ha restituito il messaggio) si lascia cadere: ora il server ce
 * l'ha col suo id.
 */
function mergeWithLocal(server: UiMessage[], prev: UiMessage[]): UiMessage[] {
  const serverIds: Record<string, true> = {};
  server.forEach((m) => { serverIds[m.id] = true; });
  const pending = prev.filter(
    (m) => !!m.status && !serverIds[m.id] && !(m.status === 'sent' && isTempId(m.id))
  );
  return pending.length > 0 ? server.concat(pending) : server;
}

/** Un messaggio in coda, come bolla. */
function outboxToUi(item: OutboxItem): UiMessage {
  const msg: UiMessage = {
    id: item.id,
    type: 'chat',
    senderType: 'guest',
    senderName: item.senderName,
    message: item.text || CHAT_PHOTO_ONLY_TEXT,
    isRead: false,
    readAt: null,
    createdAt: item.createdAt,
    reactions: [],
    status: 'queued',
  };
  if (item.attachments && item.attachments.length > 0) {
    msg.attachments = item.attachments;
    if (item.localThumbs && item.localThumbs.length > 0) msg.localThumbs = item.localThumbs;
  }
  return msg;
}

type ApiFailure = { errorCode?: string; error?: string };
const isSessionExpired = (r: ApiFailure) =>
  r.errorCode === 'SESSION_EXPIRED' || !!r.error?.includes('expired') || !!r.error?.includes('Session');

// Una "traduzione" uguale all'originale non e' una traduzione: un ospite
// italiano con l'italiano scelto non deve leggere "Tradotto" sotto ogni
// messaggio (MyMemory rimanda lo stesso testo, a volte con spazi o
// maiuscole diverse).
const normalizeForCompare = (s: string | null | undefined): string => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();

function translationChanged(fields: TranslatedMessageFields, msg: ChatMessage): boolean {
  const shown = fields.previewText || fields.message;
  const original = msg.previewText || msg.message;
  if (normalizeForCompare(shown) !== normalizeForCompare(original)) return true;
  return !!msg.subject && normalizeForCompare(fields.subject ?? msg.subject) !== normalizeForCompare(msg.subject);
}

// Per-message translation state
interface MessageTranslationState {
  translatedFields: TranslatedMessageFields | null;
  /** Card di consegna: la nota tradotta, quando non e' gia' dentro il testo. */
  translatedNote?: string | null;
  isTranslating: boolean;
  showOriginal: boolean; // true = show original, false = show translated
  error: boolean;
}


/** L'esito di un invio (deliver). */
type DeliverResult = 'sent' | 'failed' | 'expired';

const GuestChatDrawer: React.FC<GuestChatDrawerProps> = ({
  isOpen,
  onClose,
  token,
  guestName,
  onSessionExpired,
  onRelogin,
  isNonRefundable,
  initialDraft,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // La chat copre la pagina: la barra di stato prende il blu della sua testata (lib/statusBar).
  useStatusBarOverlay(CHAT_HEADER_COLOR, isOpen, { dark: CHAT_HEADER_DARK_COLOR });
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  useEffect(() => {
    if (!isOpen || !initialDraft) return;
    setNewMessage((current) => (current.trim() ? current : initialDraft));
  }, [isOpen, initialDraft]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewPill, setShowNewPill] = useState(false);
  // Debounce per-message reaction requests so rapid taps coalesce into a
  // single server call (server is last-write-wins, no 409s expected).
  const reactionDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // I genitori passano le callback come funzioni nuove a ogni render:
  // tenute in un ref, loadMessages resta stabile e gli effetti che ne
  // dipendono (caricamento all'apertura, polling) non ripartono a ogni
  // render della pagina sotto.
  const onSessionExpiredRef = useRef(onSessionExpired);
  onSessionExpiredRef.current = onSessionExpired;
  const onReloginRef = useRef(onRelogin);
  onReloginRef.current = onRelogin;
  const tRef = useRef(t);
  tRef.current = t;

  // Stato del polling e della lista (ref: non devono causare render).
  const loadSeqRef = useRef(0);
  const knownIdsRef = useRef<Set<string> | null>(null);
  const lastActivityRef = useRef(0);
  const sendingRef = useRef(false);
  const markingReadRef = useRef(false);
  const pollControlRef = useRef<{ reschedule: () => void; refreshNow: () => void } | null>(null);

  // Scorrimento
  const listRef = useRef<HTMLDivElement | null>(null);
  const stickRef = useRef(true);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const lastSeenIdRef = useRef<string | null>(null);

  // Composer
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Il contenitore della chat: all'apertura il focus va qui (non sul "‹",
  // che mostrerebbe l'anello, ne' sulla casella, che aprirebbe la tastiera).
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Foto: quelle in partenza (striscia sopra la casella), l'avviso sotto la
  // striscia e la foto aperta nel visore.
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [photoNotice, setPhotoNotice] = useState<string | null>(null);
  const [viewerPhoto, setViewerPhoto] = useState<ViewerPhoto | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  // I file da caricare restano fuori dallo stato: servono solo per il
  // caricamento e per riprovarlo. Si liberano appena la foto e' sul server.
  const photoBlobsRef = useRef<Record<string, { blob: Blob; filename: string }>>({});
  // Le foto ancora nella striscia: i passi asincroni (compressione,
  // caricamento) controllano qui se l'ospite l'ha tolta nel frattempo.
  const pendingIdsRef = useRef<Set<string>>(new Set());
  // Compressione e caricamento in fila, una foto alla volta: sulla rete
  // mobile caricarne sei insieme le farebbe fallire tutte.
  const compressChainRef = useRef<Promise<void>>(Promise.resolve());
  const uploadChainRef = useRef<Promise<void>>(Promise.resolve());
  // Object URL creati (tutti, per liberarli alla fine) e quelli gia' visti a
  // schermo (liberati appena nessuno li mostra piu').
  const createdUrlsRef = useRef<Set<string>>(new Set());
  const shownUrlsRef = useRef<Set<string>>(new Set());

  // ---------- Rete, tastiera, sessione scaduta ----------

  const { online, markOnline } = useOnlineStatus();
  const onlineRef = useRef(online);
  onlineRef.current = online;
  // La parte visibile dello schermo con la tastiera aperta (null senza).
  const viewport = useKeyboardViewport(isOpen);
  const keyboardOpen = !!viewport && viewport.keyboard;

  // La sessione scaduta vale per il token che il server ha respinto: con un
  // token nuovo (nuovo accesso) la chat torna normale da sola.
  const [expiredToken, setExpiredToken] = useState<string | null>(null);
  const expired = expiredToken !== null && (!token || token === expiredToken);
  const expiredRef = useRef(expired);
  expiredRef.current = expired;
  const expiredNotifiedRef = useRef<string | null>(null);
  const [reloginOpen, setReloginOpen] = useState(false);

  const markExpired = useCallback(() => {
    const current = tokenRef.current;
    if (!current) return;
    setExpiredToken(current);
    if (expiredNotifiedRef.current === current) return;
    expiredNotifiedRef.current = current;
    // Con il suo login il genitore lo sa subito (la Home esce dalla sessione
    // ma tiene aperta la chat). Senza, la card e il login li fa la chat, e
    // la chat non si chiude.
    if (onReloginRef.current && onSessionExpiredRef.current) onSessionExpiredRef.current();
  }, []);

  const handleRelogin = () => {
    if (onReloginRef.current) onReloginRef.current();
    else setReloginOpen(true);
  };

  // Il login della chat non sopravvive alla chiusura, ne' al nuovo accesso.
  useEffect(() => {
    if (!isOpen || !expired) setReloginOpen(false);
  }, [isOpen, expired]);

  // ---------- La coda dei messaggi scritti senza rete ----------

  // La coda sta fuori dal componente (chatOutbox): qui basta sapere quando cambia.
  const [outboxVersion, setOutboxVersion] = useState(0);
  useEffect(() => subscribeOutbox(() => setOutboxVersion((v) => v + 1)), []);
  const owner = useMemo(() => (token ? outboxOwner(token) : null), [token]);
  const queuedMessages = useMemo(
    () => outboxFor(owner).map(outboxToUi),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [owner, outboxVersion],
  );
  // La lista a schermo: quella del server (con i messaggi in invio e i
  // falliti) e, in fondo, quelli in coda.
  const shownMessages = useMemo(() => {
    if (queuedMessages.length === 0) return messages;
    const ids: Record<string, true> = {};
    messages.forEach((m) => { ids[m.id] = true; });
    return messages.concat(queuedMessages.filter((m) => !ids[m.id]));
  }, [messages, queuedMessages]);

  // Un'altra prenotazione (un altro accesso): i messaggi locali di quella di
  // prima (in invio, falliti) non si mostrano qui. La sua coda resta sua.
  const lastOwnerRef = useRef<string | null>(null);
  useEffect(() => {
    if (!owner) return;
    const previous = lastOwnerRef.current;
    lastOwnerRef.current = owner;
    if (previous && previous !== owner) setMessages((prev) => prev.filter((m) => !m.status));
  }, [owner]);

  // Translation state
  const [translationLang, setTranslationLang] = useState<TranslationLangCode | null>(null);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [translationStates, setTranslationStates] = useState<Record<string, MessageTranslationState>>({});
  const langDropdownRef = useRef<HTMLDivElement>(null);

  // Initialize translation language on mount
  useEffect(() => {
    setTranslationLang(getInitialTranslationLang());
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target as Node)) {
        setShowLangDropdown(false);
      }
    };
    if (showLangDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLangDropdown]);

  // ---------- Formati di data e ora ----------

  const lang = i18n.language;
  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(intlLocale(lang), { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ }),
    [lang]
  );
  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(intlLocale(lang), { weekday: 'long', day: 'numeric', month: 'long', timeZone: TZ }),
    [lang]
  );
  const dayYearFmt = useMemo(
    () => new Intl.DateTimeFormat(intlLocale(lang), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: TZ }),
    [lang]
  );

  // Nella bolla solo HH:MM: il giorno lo dice il separatore sopra.
  const timeLabel = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : timeFmt.format(d);
  };

  const todayKey = romeDayKey(new Date());
  const yesterdayKey = shiftDayKey(todayKey, -1);

  const dayLabel = (iso: string, key: string): string => {
    if (key && key === todayKey) return t('guestChat.today');
    if (key && key === yesterdayKey) return t('guestChat.yesterday');
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const sameYear = key.slice(0, 4) === todayKey.slice(0, 4);
    return capitalizeFirst((sameYear ? dayFmt : dayYearFmt).format(d));
  };

  // ---------- Catalogo degli extra (per le risposte rapide) ----------

  // null = non ancora arrivato o non raggiungibile: si mostrano le risposte
  // rapide di sempre. Con il catalogo diventano "intelligenti".
  const [extrasCatalog, setExtrasCatalog] = useState<ExtrasCatalog | null>(null);
  // Il catalogo non e' arrivato (rete, server): si torna alle risposte di sempre.
  const [catalogFailed, setCatalogFailed] = useState(false);
  const [stayDates, setStayDates] = useState<StayDates | null>(null);
  // Cresce quando un acquisto cambia il catalogo (push `nva_extras_update`).
  const [catalogVersion, setCatalogVersion] = useState(0);
  // L'avviso sotto le risposte rapide ("late check-out non disponibile").
  const [quickNotice, setQuickNotice] = useState<string | null>(null);
  // Il vino di giorno: si estrae UNA volta per apertura della chat e resta
  // quello finché la chat è aperta (le proposte non "ballano" a ogni render).
  const daytimeDrink = useMemo(() => isOpen && Math.random() < DAYTIME_DRINK_ODDS, [isOpen]);
  // Check-in online fatto secondo la sessione salvata (più fresca del token,
  // che dice com'era quando è stato emesso). Si rilegge a ogni apertura.
  // null = nessuna sessione salvata per questo token: vale il token.
  const sessionCheckIn = useMemo(() => (isOpen && token ? storedCheckInState(token) : null), [isOpen, token]);

  useEffect(() => {
    if (!isOpen || !token) return;
    let cancelled = false;
    const key = quickCatalogKey(token, lang);
    const cached = quickCatalogCache;
    if (cached && cached.key === key && Date.now() - cached.at < QUICK_CATALOG_TTL_MS) {
      setExtrasCatalog(cached.catalog);
      return;
    }
    // Il catalogo di un'altra prenotazione o di un'altra lingua non vale:
    // meglio le risposte di sempre finche' non arriva quello giusto. Uno
    // solo scaduto invece resta, e si sostituisce in silenzio.
    if (!cached || cached.key !== key) setExtrasCatalog(null);
    setCatalogFailed(false);
    fetchBookingExtras(token, lang)
      .then((catalog) => {
        if (cancelled) return;
        if (catalog.error) {
          setCatalogFailed(true);
          return;
        }
        quickCatalogCache = { key, at: Date.now(), catalog };
        setExtrasCatalog(catalog);
      })
      .catch(() => { if (!cancelled) setCatalogFailed(true); });
    return () => { cancelled = true; };
  }, [isOpen, token, lang, catalogVersion]);

  // Le date del soggiorno (arrivo, partenza) per scegliere le proposte: di
  // solito dal token stesso, senza rete. Una volta per prenotazione.
  const stayDatesToken = stayDates ? stayDates.token : null;
  useEffect(() => {
    if (!isOpen || !token || stayDatesToken === token) return;
    let cancelled = false;
    fetchBookingInfo(token)
      .then((result) => {
        if (cancelled || !result.success || !result.booking) return;
        const { checkIn, checkOut } = result.booking;
        if (checkIn && checkOut) {
          setStayDates({ token, checkIn, checkOut, checkedIn: !!result.booking.onlineCheckInCompleted });
        }
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [isOpen, token, stayDatesToken]);

  // Un acquisto (o il late check-out che matura) cambia le proposte: il
  // catalogo in memoria non vale piu'.
  useEffect(() => {
    const onExtrasUpdate = () => {
      quickCatalogCache = null;
      setCatalogVersion((v) => v + 1);
    };
    window.addEventListener('nva_extras_update', onExtrasUpdate);
    return () => window.removeEventListener('nva_extras_update', onExtrasUpdate);
  }, []);

  // L'avviso se ne va da solo, quando l'ospite scrive o chiude la chat.
  useEffect(() => {
    if (!quickNotice) return;
    const id = setTimeout(() => setQuickNotice(null), 8000);
    return () => clearTimeout(id);
  }, [quickNotice]);
  useEffect(() => {
    if (!isOpen || newMessage.length > 0) setQuickNotice(null);
  }, [isOpen, newMessage]);

  // ---------- Caricamento ----------

  const loadMessages = useCallback(async (showLoader = true) => {
    if (!token) return;

    const seq = ++loadSeqRef.current;
    if (showLoader) setIsLoading(true);

    const result = await fetchChatMessages(token);

    // Con il polling veloce due richieste possono incrociarsi: una risposta
    // arrivata dopo una piu' recente non deve riportare indietro la lista.
    if (seq !== loadSeqRef.current) {
      if (showLoader) setIsLoading(false);
      return;
    }

    if (result.success && result.messages) {
      const serverMessages = result.messages as UiMessage[];
      const now = Date.now();

      // Il server ha risposto: la rete c'e', anche se il telefono dice di no
      // (e allora parte la coda).
      if (!onlineRef.current) markOnline();

      // "Ricevuto": un messaggio dello staff mai visto prima riaccende il
      // polling veloce. Conta anche l'ultimo messaggio in assoluto (limitato
      // ad "adesso", se l'orologio del telefono e' indietro): chi apre la
      // chat subito dopo una risposta parte gia' dalla cadenza veloce.
      const activityBefore = lastActivityRef.current;
      const known = knownIdsRef.current;
      if (known) {
        for (let i = 0; i < serverMessages.length; i++) {
          const m = serverMessages[i];
          if (m.senderType !== 'guest' && !known.has(m.id)) {
            lastActivityRef.current = now;
            break;
          }
        }
      }
      knownIdsRef.current = new Set(serverMessages.map((m) => m.id));
      const last = serverMessages[serverMessages.length - 1];
      if (last) {
        const ts = Date.parse(last.createdAt);
        if (!isNaN(ts)) lastActivityRef.current = Math.max(lastActivityRef.current, Math.min(ts, now));
      }
      // Il prossimo giro era stato programmato con l'attivita' di prima (al
      // primo caricamento: nessuna, quindi 60s): si riprogramma con quella nuova.
      if (lastActivityRef.current !== activityBefore) pollControlRef.current?.reschedule();

      setError(null);
      setMessages((prev) => mergeWithLocal(serverMessages, prev));
    } else if (isSessionExpired(result)) {
      markExpired();
    } else {
      setError(result.error || tRef.current('guestChat.error'));
    }

    if (showLoader) setIsLoading(false);
  }, [token, markOnline, markExpired]);

  // Load messages when drawer opens (e dopo un nuovo accesso: token nuovo)
  useEffect(() => {
    if (isOpen && token && !expired) {
      loadMessages();
    }
  }, [isOpen, token, expired, loadMessages]);

  // Polling adattivo (vedi POLL_*). Si ferma quando l'app non si vede e
  // riparte subito al ritorno; una push di chat (App.tsx inoltra i
  // messaggi/reazioni dello staff come evento `nva_chat_reaction`) ricarica
  // all'istante invece di aspettare il giro. Anche il ritorno della rete
  // ricarica subito. Con la sessione scaduta non si interroga piu' il server.
  useEffect(() => {
    if (!isOpen || !token || expired) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = 0;

    const clear = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const schedule = () => {
      clear();
      if (cancelled || document.hidden) return;
      timer = setTimeout(() => run(false), pollDelay(lastActivityRef.current));
    };
    // force: una richiesta esplicita (ritorno nell'app, push) parte anche se
    // un giro e' ancora in corso, perche' quello potrebbe essere partito
    // prima della novita'. Il giro a tempo invece salta: niente richieste
    // accavallate, e niente GET mentre l'ospite sta spedendo (la lista
    // mostrerebbe il messaggio due volte finche' la POST non risponde).
    const run = (force: boolean) => {
      timer = null;
      if (cancelled || document.hidden) return;
      if (!force && (inFlight > 0 || sendingRef.current)) {
        schedule();
        return;
      }
      inFlight++;
      loadMessages(false)
        .catch(() => undefined)
        .then(() => {
          inFlight--;
          if (!cancelled) schedule();
        });
    };
    const refreshNow = () => {
      clear();
      run(true);
    };
    const onVisibilityChange = () => {
      if (document.hidden) clear();
      else refreshNow();
    };
    const onChatPush = () => {
      lastActivityRef.current = Date.now();
      refreshNow();
    };

    pollControlRef.current = { reschedule: schedule, refreshNow };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('nva_chat_reaction', onChatPush);
    window.addEventListener('online', refreshNow);
    schedule();

    return () => {
      cancelled = true;
      clear();
      pollControlRef.current = null;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('nva_chat_reaction', onChatPush);
      window.removeEventListener('online', refreshNow);
    };
  }, [isOpen, token, expired, loadMessages]);

  // Letto = l'ospite ha davvero la chat davanti. La GET non segna piu'
  // niente (il badge la chiama anche a chat chiusa), quindi la PUT parte solo
  // con il drawer aperto, l'app visibile e almeno un messaggio dello staff
  // ancora da leggere: niente chiamate a vuoto a ogni giro di polling.
  useEffect(() => {
    if (!isOpen || !token || expired || markingReadRef.current) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    const hasUnread = messages.some((m) => m.senderType === 'admin' && m.isRead === false);
    if (!hasUnread) return;

    markingReadRef.current = true;
    markMessagesAsRead(token).then((ok) => {
      markingReadRef.current = false;
      if (!ok) return;
      const readAt = new Date().toISOString();
      setMessages((prev) => prev.map((m): UiMessage => (
        m.senderType === 'admin' && m.isRead === false ? { ...m, isRead: true, readAt: m.readAt || readAt } : m
      )));
      // Il badge (useUnreadMessages) si azzera senza aspettare il suo giro.
      try { window.dispatchEvent(new CustomEvent('nva_chat_read')); } catch { /* ambienti senza CustomEvent */ }
    });
  }, [isOpen, token, expired, messages]);

  // Auto-translate admin/system messages when language changes or new messages arrive
  const appLang = appLanguageOf(lang);
  useEffect(() => {
    if (!translationLang || messages.length === 0) return;

    // Le foto senza testo ("📷") non si traducono: non c'e' niente da tradurre.
    // Le card di consegna e d'ordine sono scritte nella lingua dell'app: si
    // traducono solo se la chat traduce verso un'ALTRA lingua (orario e
    // articoli restano nella card, sotto compare il testo tradotto).
    const adminMessages = messages.filter((msg) => {
      if (msg.senderType === 'guest' || isPhotoOnlyText(msg.message)) return false;
      if (isCardKind(msg.kind)) return cardLanguageOf(msg, appLang) !== translationLang;
      return true;
    });

    adminMessages.forEach((msg) => {
      const existing = translationStates[msg.id];
      // Skip if already translated for this language, or currently translating
      if (existing?.translatedFields && !existing.error) return;
      if (existing?.isTranslating) return;

      // Start translating
      setTranslationStates((prev) => ({
        ...prev,
        [msg.id]: {
          translatedFields: null,
          isTranslating: true,
          showOriginal: false,
          error: false,
        },
      }));

      const card = isCardKind(msg.kind);
      // La nota dello staff di solito e' gia' dentro il testo del messaggio
      // (" Nota: ..."): allora basta tradurre quello. Se non c'e', seconda
      // richiesta a parte (con una chiave di cache sua).
      const note = card ? cardNoteOf(msg) : '';
      const noteApart = !!note && msg.message.indexOf(note) < 0;

      Promise.all([
        translateMessageFields(msg.id, card ? { message: msg.message } : {
          message: msg.message,
          subject: msg.subject,
          previewText: msg.previewText,
        }, translationLang),
        noteApart
          ? translateMessageFields(`${msg.id}:note`, { message: note }, translationLang).then((r) => r.message)
          : Promise.resolve(null),
      ]).then(([translated, translatedNote]) => {
        setTranslationStates((prev) => ({
          ...prev,
          [msg.id]: {
            translatedFields: translated,
            translatedNote,
            isTranslating: false,
            showOriginal: false,
            error: false,
          },
        }));
      }).catch(() => {
        setTranslationStates((prev) => ({
          ...prev,
          [msg.id]: {
            translatedFields: null,
            isTranslating: false,
            showOriginal: true,
            error: true,
          },
        }));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationLang, messages, appLang]);

  // When translation language changes, clear old translations and re-translate
  const handleLanguageChange = (code: TranslationLangCode | null) => {
    setTranslationLang(code);
    saveTranslationLang(code);
    setTranslationStates({}); // Clear all translation states to force re-translate
    setShowLangDropdown(false);
  };

  // Toggle original/translated for a specific message
  const toggleMessageTranslation = (messageId: string) => {
    setTranslationStates((prev) => {
      const existing = prev[messageId];
      if (!existing) return prev;
      return {
        ...prev,
        [messageId]: {
          ...existing,
          showOriginal: !existing.showOriginal,
        },
      };
    });
  };

  // Cleanup any pending reaction debounces on unmount to avoid setState on
  // an unmounted component.
  useEffect(() => {
    const timeouts = reactionDebounceRef.current;
    return () => {
      Object.keys(timeouts).forEach((id) => clearTimeout(timeouts[id]));
    };
  }, []);

  // Expose drawer open-state on window so the App-level OneSignal listener
  // can suppress redundant foreground banners when the user is already
  // looking at the chat.
  useEffect(() => {
    (window as unknown as { __nvaGuestChatOpen?: boolean }).__nvaGuestChatOpen = isOpen;
    return () => {
      (window as unknown as { __nvaGuestChatOpen?: boolean }).__nvaGuestChatOpen = false;
    };
  }, [isOpen]);

  // ---------- Scorrimento ----------

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = listRef.current;
    if (!el) return;
    if (smooth && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  // Ref a callback: il contenuto del Sheet si monta a ogni apertura, e un
  // effetto legato a isOpen girerebbe prima che la lista esista. Prima si
  // scorreva solo quando cambiava il numero dei messaggi, quindi riaprendo
  // la chat si ripartiva dall'alto. Il ResizeObserver tiene la lista in fondo
  // quando cresce (messaggi nuovi, traduzioni, casella che si allarga,
  // tastiera che si apre), ma solo se l'ospite era gia' in fondo.
  const setListEl = useCallback((el: HTMLDivElement | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    listRef.current = el;
    if (!el) return;

    stickRef.current = true;
    el.scrollTop = el.scrollHeight;

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        const list = listRef.current;
        if (list && stickRef.current) list.scrollTop = list.scrollHeight;
      });
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
      resizeObserverRef.current = ro;
    }
  }, []);

  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const stuck = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD_PX;
    stickRef.current = stuck;
    if (stuck) setShowNewPill(false);
  }, []);

  // Ogni apertura parte dagli ultimi messaggi, senza la pillola di prima.
  useEffect(() => {
    if (isOpen) {
      stickRef.current = true;
      setShowNewPill(false);
    }
  }, [isOpen]);

  // Nuovo ultimo messaggio: se l'ospite e' in fondo si scorre (anche senza
  // ResizeObserver); se sta rileggendo piu' su, un messaggio dello staff
  // accende la pillola invece di strappargli la lista da sotto il dito.
  const lastMsg = shownMessages.length > 0 ? shownMessages[shownMessages.length - 1] : null;
  const lastId = lastMsg ? lastMsg.id : null;
  const lastIsGuest = lastMsg ? lastMsg.senderType === 'guest' : false;
  useLayoutEffect(() => {
    const prevId = lastSeenIdRef.current;
    lastSeenIdRef.current = lastId;
    if (!lastId || prevId === lastId) return;
    if (stickRef.current) {
      scrollToBottom(false);
      return;
    }
    if (prevId !== null && !lastIsGuest) setShowNewPill(true);
  }, [lastId, lastIsGuest, scrollToBottom]);

  const handleNewPillClick = () => {
    stickRef.current = true;
    setShowNewPill(false);
    scrollToBottom(true);
  };

  // ---------- Reazioni ----------

  // Optimistically toggle the guest's reaction on a message and reconcile with
  // the server response. On failure we refetch from the server so the UI
  // converges to authoritative state even if the user tapped multiple times
  // before the request resolved (rolling back to a captured snapshot would
  // restore stale intermediate state in that case).
  const handleReact = useCallback((messageId: string, emoji: string) => {
    if (!token) return;

    setMessages((prev) => prev.map((msg) => {
      if (msg.id !== messageId) return msg;

      const reactions = msg.reactions ? [...msg.reactions] : [];
      const ownIdx = reactions.findIndex((r) => r.senderType === 'guest');
      const own = ownIdx >= 0 ? reactions[ownIdx] : null;

      if (own && own.emoji === emoji) {
        // Toggle off
        reactions.splice(ownIdx, 1);
      } else {
        const next: MessageReaction = {
          emoji,
          senderType: 'guest',
          senderName: guestName || t('common.guest'),
          createdAt: new Date().toISOString(),
        };
        if (ownIdx >= 0) {
          reactions[ownIdx] = next;
        } else {
          reactions.push(next);
        }
      }
      return { ...msg, reactions };
    }));

    // Coalesce rapid taps on the same message
    const pending = reactionDebounceRef.current[messageId];
    if (pending) clearTimeout(pending);

    reactionDebounceRef.current[messageId] = setTimeout(async () => {
      delete reactionDebounceRef.current[messageId];
      const result = await sendReaction(token, messageId, emoji);

      if (result.success && result.reactions) {
        // Trust the server's full reactions array (handles cross-side state)
        const serverReactions = result.reactions;
        setMessages((prev) => prev.map((msg) =>
          msg.id === messageId ? { ...msg, reactions: serverReactions } : msg
        ));
      } else if (isSessionExpired(result)) {
        markExpired();
      } else if (!result.success) {
        // Refetch from the server to recover authoritative state.
        setError(result.error || t('guestChat.reactions.error'));
        loadMessages(false);
      }
    }, 300);
  }, [token, guestName, t, loadMessages, markExpired]);

  // ---------- Invio ----------

  // La POST vera. Il messaggio temporaneo e' gia' in lista: qui lo si
  // sostituisce con quello del server (id vero, orario del server) oppure lo
  // si segna fallito. Con la sessione scaduta (401) il server non l'ha
  // preso: torna in testa alla coda e parte dopo il nuovo accesso.
  const deliver = useCallback(async (temp: UiMessage, text: string, attachments?: ChatAttachment[]): Promise<DeliverResult> => {
    const currentToken = tokenRef.current;
    if (!currentToken) {
      setMessages((prev) => prev.map((m): UiMessage => (m.id === temp.id ? { ...m, status: 'failed' } : m)));
      return 'failed';
    }
    const tempId = temp.id;
    sendingRef.current = true;
    setIsSending(true);

    const result = await sendChatMessage(currentToken, text, attachments);

    sendingRef.current = false;
    setIsSending(false);

    if (result.success) {
      const real = result.message && result.message.id ? result.message : null;
      if (real) preloadChatThumbs(real.attachments);
      setMessages((prev) => {
        // Il polling puo' averlo gia' portato: niente doppione.
        if (real && prev.some((m) => m.id === real.id)) return prev.filter((m) => m.id !== tempId);
        return prev.map((m): UiMessage => {
          if (m.id !== tempId) return m;
          if (!real) return { ...m, status: 'sent' };
          // Le miniature locali restano finche' il messaggio non arriva
          // dalla lista; le foto, se il server non le rimanda, sono le nostre.
          const serverAtts = Array.isArray(real.attachments) && real.attachments.length > 0 ? real.attachments : m.attachments;
          return { ...real, attachments: serverAtts, localThumbs: m.localThumbs, status: 'sent' };
        });
      });
      // Senza il messaggio nella risposta lo si prende dal server subito.
      if (!real) pollControlRef.current?.refreshNow();
      return 'sent';
    }
    if (isSessionExpired(result)) {
      enqueueOutbox({
        id: tempId,
        owner: outboxOwner(currentToken),
        text,
        attachments,
        localThumbs: temp.localThumbs,
        senderName: temp.senderName,
        createdAt: temp.createdAt,
      }, true);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      markExpired();
      return 'expired';
    }
    setMessages((prev) => prev.map((m): UiMessage => (m.id === tempId ? { ...m, status: 'failed' } : m)));
    setError(result.error || tRef.current('guestChat.sendError'));
    return 'failed';
  }, [markExpired]);

  // La coda parte: un messaggio alla volta, nell'ordine in cui e' stato
  // scritto. Ognuno esce dalla coda prima di partire (takeFromOutbox), cosi'
  // non parte mai due volte. Al primo che non va la coda si ferma: se la
  // rete e' di nuovo giu' gli altri restano in coda; se no diventano "Non
  // inviato · Tocca per riprovare" (niente orologi fermi con la rete).
  const flushingRef = useRef(false);
  const flushQueue = useCallback(async () => {
    if (flushingRef.current) return;
    const startToken = tokenRef.current;
    if (!startToken || expiredRef.current || !onlineRef.current) return;
    flushingRef.current = true;
    try {
      for (;;) {
        const currentToken = tokenRef.current;
        if (!currentToken || currentToken !== startToken || expiredRef.current) break;
        const next = outboxFor(outboxOwner(currentToken))[0];
        if (!next) break;
        const item = takeFromOutbox(next.id);
        if (!item) continue;
        const temp: UiMessage = { ...outboxToUi(item), status: 'sending' };
        stickRef.current = true;
        setMessages((prev) => (prev.some((m) => m.id === temp.id) ? prev : [...prev, temp]));
        lastActivityRef.current = Date.now();
        pollControlRef.current?.reschedule();
        const atts = item.attachments && item.attachments.length > 0 ? item.attachments : undefined;
        const outcome = await deliver(temp, item.text, atts);
        if (outcome === 'sent') continue;
        if (outcome === 'failed' && isNavigatorOnline()) {
          const rest = outboxFor(outboxOwner(currentToken))
            .map((queued) => takeFromOutbox(queued.id))
            .filter((queued): queued is OutboxItem => !!queued)
            .map((queued): UiMessage => ({ ...outboxToUi(queued), status: 'failed' }));
          if (rest.length > 0) setMessages((prev) => prev.concat(rest.filter((m) => !prev.some((p) => p.id === m.id))));
        }
        break;
      }
    } finally {
      flushingRef.current = false;
    }
  }, [deliver]);

  // La coda parte quando c'e' tutto: rete, token valido, qualcosa in coda.
  // Anche a chat chiusa (il componente resta montato nella pagina).
  useEffect(() => {
    if (!online || !token || expired) return;
    if (outboxFor(owner).length === 0) return;
    void flushQueue();
  }, [online, token, expired, owner, outboxVersion, flushQueue]);

  // Invio ottimistico: la bolla compare subito (con l'orologio), la casella
  // si svuota e la lista va in fondo; il server conferma dopo.
  // Con le foto (gia' caricate) il testo puo' mancare: la bolla mostra le
  // miniature locali e il server mettera' "📷" come testo.
  // Senza rete (o con altri messaggi ancora in coda, per non superarli) il
  // messaggio va in coda e parte da solo quando si puo'.
  const sendText = useCallback((raw: string, attachments?: ChatAttachment[], localThumbs?: string[]): boolean => {
    const text = raw.trim();
    const atts = attachments && attachments.length > 0 ? attachments : undefined;
    if ((!text && !atts) || !token || expiredRef.current) return false;
    const queueOwner = outboxOwner(token);
    const mustQueue = !onlineRef.current || flushingRef.current || outboxFor(queueOwner).length > 0;
    if (!mustQueue && sendingRef.current) return false;

    const temp: UiMessage = {
      id: `${TEMP_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: 'chat',
      senderType: 'guest',
      senderName: guestName || tRef.current('common.guest'),
      message: text || CHAT_PHOTO_ONLY_TEXT,
      isRead: false,
      readAt: null,
      createdAt: new Date().toISOString(),
      reactions: [],
      status: 'sending',
    };
    if (atts) {
      temp.attachments = atts;
      if (localThumbs && localThumbs.length > 0) temp.localThumbs = localThumbs;
    }

    stickRef.current = true;
    setShowNewPill(false);
    setError(null);

    if (mustQueue) {
      enqueueOutbox({
        id: temp.id,
        owner: queueOwner,
        text,
        attachments: atts,
        localThumbs: temp.localThumbs,
        senderName: temp.senderName,
        createdAt: temp.createdAt,
      });
      return true;
    }

    setMessages((prev) => [...prev, temp]);
    // L'ospite ha scritto: la risposta puo' arrivare a momenti, polling veloce da adesso.
    lastActivityRef.current = Date.now();
    pollControlRef.current?.reschedule();
    void deliver(temp, text, atts);
    return true;
  }, [token, guestName, deliver]);

  const retryMessage = useCallback((msg: UiMessage) => {
    if (!token || sendingRef.current || expiredRef.current) return;
    // Il riprovato torna in fondo: parte adesso, dopo quello che e' arrivato nel frattempo.
    setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    // Le foto sono gia' sul server: si rispedisce solo il messaggio, con le
    // stesse foto e le stesse miniature locali. Il "📷" lo rimette il server.
    const atts = msg.attachments && msg.attachments.length > 0 ? msg.attachments : undefined;
    const text = atts && isPhotoOnlyText(msg.message) ? '' : msg.message;
    sendText(text, atts, msg.localThumbs);
  }, [token, sendText]);

  // ---------- Foto ----------

  const patchPhoto = useCallback((id: string, patch: Partial<PendingPhoto>) => {
    setPendingPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  const uploadPhoto = useCallback(async (id: string) => {
    const entry = photoBlobsRef.current[id];
    if (!entry || !pendingIdsRef.current.has(id)) return;
    const currentToken = tokenRef.current;
    if (!currentToken) {
      patchPhoto(id, { state: 'failed', retryable: true });
      return;
    }
    patchPhoto(id, { state: 'uploading' });

    const result = await uploadChatPhoto(currentToken, entry.blob, entry.filename);

    // Tolta dalla striscia mentre si caricava: il risultato non serve piu'.
    if (!pendingIdsRef.current.has(id)) return;
    if (result.success && result.attachment) {
      delete photoBlobsRef.current[id];
      patchPhoto(id, { state: 'ready', attachment: result.attachment });
      return;
    }
    // Troppo grande o formato sbagliato: riprovare non serve, la si puo'
    // solo togliere. Il testo viene dal codice, nella lingua dell'app.
    patchPhoto(id, { state: 'failed', retryable: isChatPhotoErrorRetryable(result.errorCode), errorCode: result.errorCode });
    if (isSessionExpired(result)) {
      markExpired();
    } else {
      setPhotoNotice(tRef.current(chatPhotoErrorKey(result.errorCode)));
    }
  }, [patchPhoto, markExpired]);

  const enqueueUpload = useCallback((id: string) => {
    uploadChainRef.current = uploadChainRef.current
      .then(() => uploadPhoto(id))
      .catch(() => undefined);
  }, [uploadPhoto]);

  // Comprime, mostra l'anteprima e mette la foto in fila per il caricamento.
  const preparePhoto = useCallback(async (id: string, file: File) => {
    if (!pendingIdsRef.current.has(id)) return;
    let blob: Blob = await compressImage(file, { maxSide: PHOTO_MAX_SIDE, quality: PHOTO_QUALITY });
    if (!pendingIdsRef.current.has(id)) return;
    const filename = uploadFilename(file, blob);
    // Alcuni Android passano l'HEIC senza tipo: senza, il server non lo riconosce.
    if (blob === file && !file.type && isHeic(file)) blob = file.slice(0, file.size, 'image/heic');

    let previewUrl: string | null = null;
    try {
      previewUrl = URL.createObjectURL(blob);
      createdUrlsRef.current.add(previewUrl);
    } catch {
      previewUrl = null;
    }
    if (blob.size > CHAT_PHOTO_MAX_BYTES) {
      patchPhoto(id, { previewUrl, state: 'failed', retryable: false, errorCode: 'file_too_large' });
      setPhotoNotice(tRef.current('guestChat.photo.tooLarge'));
      return;
    }
    photoBlobsRef.current[id] = { blob, filename };
    patchPhoto(id, { previewUrl, state: 'uploading' });
    enqueueUpload(id);
  }, [patchPhoto, enqueueUpload]);

  const handleFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const files = input.files ? Array.from(input.files) : [];
    // Svuotato subito: riscegliere la stessa foto deve funzionare.
    input.value = '';
    const images = files.filter((f) => !f.type || f.type.indexOf('image/') === 0 || isHeic(f));
    if (images.length === 0) return;

    const free = MAX_PHOTOS_PER_MESSAGE - pendingIdsRef.current.size;
    const taken = images.slice(0, Math.max(0, free));
    setPhotoNotice(images.length > taken.length ? t('guestChat.photo.limit') : null);
    if (taken.length === 0) return;

    const added: PendingPhoto[] = taken.map((_, i) => ({
      id: `ph-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
      previewUrl: null,
      state: 'processing',
    }));
    added.forEach((p) => pendingIdsRef.current.add(p.id));
    setPendingPhotos((prev) => prev.concat(added));

    added.forEach((p, i) => {
      const file = taken[i];
      compressChainRef.current = compressChainRef.current
        .then(() => preparePhoto(p.id, file))
        .catch(() => undefined);
    });
  };

  const retryPhoto = useCallback((id: string) => {
    if (!photoBlobsRef.current[id]) return;
    setPhotoNotice(null);
    patchPhoto(id, { state: 'uploading', retryable: undefined, errorCode: undefined });
    enqueueUpload(id);
  }, [patchPhoto, enqueueUpload]);

  const removePhoto = useCallback((id: string) => {
    pendingIdsRef.current.delete(id);
    delete photoBlobsRef.current[id];
    setPendingPhotos((prev) => prev.filter((p) => p.id !== id));
    setPhotoNotice(null);
  }, []);

  // Gli object URL si liberano appena nessuno li mostra piu' (foto tolta
  // dalla striscia, bolla sostituita dal messaggio del server). Si guarda
  // solo quelli gia' comparsi a schermo: uno appena creato potrebbe non
  // essere ancora arrivato nello stato. Le bolle in coda li usano ancora.
  useEffect(() => {
    const inUse = new Set<string>();
    pendingPhotos.forEach((p) => {
      if (p.previewUrl) inUse.add(p.previewUrl);
    });
    shownMessages.forEach((m) => {
      if (m.localThumbs) m.localThumbs.forEach((u) => { if (u) inUse.add(u); });
    });
    const shown = shownUrlsRef.current;
    shown.forEach((u) => {
      if (inUse.has(u)) return;
      try { URL.revokeObjectURL(u); } catch { /* gia' liberato */ }
      shown.delete(u);
      createdUrlsRef.current.delete(u);
    });
    inUse.forEach((u) => shown.add(u));
  }, [pendingPhotos, shownMessages]);

  // All'uscita si libera tutto.
  useEffect(() => {
    const created = createdUrlsRef.current;
    return () => {
      created.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* gia' liberato */ }
      });
      created.clear();
    };
  }, []);

  // Il visore non sopravvive alla chiusura della chat.
  useEffect(() => {
    if (!isOpen) setViewerPhoto(null);
  }, [isOpen]);

  const closeViewer = useCallback(() => setViewerPhoto(null), []);

  const photosBusy = pendingPhotos.some((p) => p.state === 'processing' || p.state === 'uploading');
  const readyPhotos = pendingPhotos.filter((p) => p.state === 'ready' && !!p.attachment);
  const canSend = !expired && !isSending && !photosBusy && (newMessage.trim().length > 0 || readyPhotos.length > 0);

  // Partono le foto gia' caricate; quelle fallite restano nella striscia,
  // da riprovare o togliere.
  const handleSend = () => {
    if (photosBusy) return;
    const hadText = newMessage.trim().length > 0;
    const attachments = readyPhotos.map((p) => p.attachment as ChatAttachment);
    const thumbs = readyPhotos.map((p) => p.previewUrl || '');
    if (sendText(newMessage, attachments, thumbs)) {
      setNewMessage('');
      if (readyPhotos.length > 0) {
        const sentIds = readyPhotos.map((p) => p.id);
        sentIds.forEach((id) => {
          pendingIdsRef.current.delete(id);
          delete photoBlobsRef.current[id];
        });
        // Gli object URL passano alla bolla: non si liberano qui.
        setPendingPhotos((prev) => prev.filter((p) => sentIds.indexOf(p.id) === -1));
        setPhotoNotice(null);
      }
      // La casella non viene mai disabilitata, quindi il focus resta qui e
      // la tastiera non si chiude tra un messaggio e l'altro. Dopo sole
      // foto invece non la si riapre.
      if (hadText) textareaRef.current?.focus();
    }
  };

  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    // Tastiere IME (cinese, giapponese...): Invio conferma la parola, non spedisce.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (!hasFinePointer()) return;
    e.preventDefault();
    handleSend();
  };

  // La casella cresce con il testo fino a ~5 righe. Si misura anche al
  // montaggio (callback ref) perche' una bozza puo' restare tra una apertura
  // e l'altra.
  const autoGrow = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    const borders = el.offsetHeight - el.clientHeight;
    const next = Math.min(el.scrollHeight + borders, COMPOSER_MAX_HEIGHT_PX);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight + borders > COMPOSER_MAX_HEIGHT_PX ? 'auto' : 'hidden';
  };

  const setTextareaEl = useCallback((el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    if (el) autoGrow(el);
  }, []);

  useLayoutEffect(() => {
    if (textareaRef.current) autoGrow(textareaRef.current);
  }, [newMessage]);

  // ---------- Risposte rapide ----------

  const goTo = (to: string) => {
    onClose();
    navigate(to);
  };

  // Il servizio aperto direttamente nel negozio (/extras?service=...): la
  // pagina scorre fino alla sua card e apre la scheda d'acquisto.
  const goToService = (serviceId: string) => goTo(`/extras?service=${encodeURIComponent(serviceId)}`);

  // "Check-out posticipato": si va a comprarlo solo se il catalogo dice che
  // si puo' adesso; altrimenti un avviso qui, invece di una pagina che dice
  // la stessa cosa.
  const lateCheckoutBusyRef = useRef(false);
  const handleLateCheckoutChip = async () => {
    if (lateCheckoutBusyRef.current) return;
    let catalog = extrasCatalog;
    if (!catalog && token) {
      lateCheckoutBusyRef.current = true;
      const fresh = await fetchBookingExtras(token, lang).catch(() => null);
      lateCheckoutBusyRef.current = false;
      if (fresh && !fresh.error) {
        catalog = fresh;
        quickCatalogCache = { key: quickCatalogKey(token, lang), at: Date.now(), catalog: fresh };
        setExtrasCatalog(fresh);
      }
    }
    // Senza catalogo (rete assente) non lo si sa: decide la pagina degli extra.
    if (!catalog || lateCheckoutOpen(catalog) || lateCheckoutBought(catalog)) {
      goToService('late_checkout');
      return;
    }
    setQuickNotice(t('guestChat.quick.lateCheckoutUnavailable'));
  };

  /** L'etichetta di una proposta: il nome del servizio dal catalogo (gia' tradotto), se c'e'. */
  const offerLabel = (pick: OfferPick): string => {
    const name = pick.item && pick.item.name ? pick.item.name.trim() : '';
    switch (pick.serviceId) {
      case 'late_checkout': return t('guestChat.quick.lateCheckout');
      case 'caffe_brioche': return t('guestChat.quick.breakfastTomorrow');
      case 'welcome_wine': return name || t('guestChat.quick.wine');
      case 'water_bottles': return t('guestChat.quick.water');
      case 'extra_cleaning': return name || t('guestChat.quick.cleaning');
      case 'linen_supplies': return name || t('guestChat.quick.towels');
      case 'paper_supplies': return name || t('guestChat.quick.paper');
      case 'hygiene_supplies': return name || t('guestChat.quick.hygiene');
      default: return name || pick.serviceId;
    }
  };

  const wifiChip: QuickReply = { key: 'wifi', label: t('guestChat.quick.wifi'), icon: <Wifi />, onClick: () => goTo('/my-stay?open=wifi') };
  const trashChip: QuickReply = { key: 'trash', label: t('guestChat.quick.trash'), icon: <Trash2 />, onClick: () => goTo('/my-stay?open=trash') };
  // Il testo che parte resta quello di sempre (con l'emoji): cambia solo il chip.
  const thanksChip: QuickReply = { key: 'thanks', label: t('guestChat.quick.thanks'), icon: <Heart />, onClick: () => { sendText(`🙏 ${t('guestChat.quick.thanks')}`); } };

  let quickReplies: QuickReply[];
  if (extrasCatalog) {
    // Con il catalogo: Wi-Fi e Rifiuti sempre, poi al massimo due proposte
    // scelte per il momento del soggiorno (mai cose gia' prese o non
    // acquistabili adesso). Un posto rimasto libero torna al "Grazie!".
    // La sera (dalle 16) le proposte vengono prima: e' l'ora della
    // colazione per domani e del vino.
    const dates = stayDates && stayDates.token === token ? stayDates : null;
    const now = new Date();
    const offers = pickOfferChips(
      extrasCatalog,
      dates,
      now,
      { checkInDone: sessionCheckIn ?? !!(dates && dates.checkedIn), daytimeDrink },
    )
      .map((pick): QuickReply => ({
        key: `offer-${pick.serviceId}`,
        label: offerLabel(pick),
        icon: offerIcon(pick.serviceId),
        onClick: pick.pending
          ? () => setQuickNotice(t('guestChat.quick.lateCheckoutUnavailable'))
          : () => goToService(pick.serviceId),
        offer: true,
      }));
    quickReplies = romeHour(now) >= EVENING_FROM_HOUR
      ? offers.concat([wifiChip, trashChip])
      : [wifiChip, trashChip].concat(offers);
    if (quickReplies.length < MAX_QUICK_CHIPS) quickReplies.push(thanksChip);
  } else if (!catalogFailed) {
    // Il catalogo sta arrivando: intanto le due fisse, le proposte si
    // aggiungono dopo (niente chip che compaiono e poi spariscono).
    quickReplies = [wifiChip, trashChip];
  } else {
    // Catalogo non raggiungibile: le risposte di sempre.
    quickReplies = [
      { key: 'towels', label: t('guestChat.quick.towels'), icon: <Bath />, onClick: () => goToService('linen_supplies') },
      trashChip,
      { key: 'lateCheckout', label: t('guestChat.quick.lateCheckout'), icon: <Clock />, onClick: () => { void handleLateCheckoutChip(); } },
      wifiChip,
      thanksChip,
    ];
  }

  // ---------- Rendering ----------

  // Get the display text for a message, considering translation state.
  // Una traduzione uguale all'originale non conta (niente "Tradotto").
  const getDisplayFields = (msg: ChatMessage): { message: string; subject?: string; previewText?: string; isTranslated: boolean } => {
    const isGuest = msg.senderType === 'guest';

    // Never translate guest's own messages
    if (isGuest || !translationLang) {
      return { message: msg.message, subject: msg.subject, previewText: msg.previewText, isTranslated: false };
    }

    const state = translationStates[msg.id];
    if (!state || state.showOriginal || !state.translatedFields || !translationChanged(state.translatedFields, msg)) {
      return { message: msg.message, subject: msg.subject, previewText: msg.previewText, isTranslated: false };
    }

    return {
      message: state.translatedFields.message,
      subject: state.translatedFields.subject ?? msg.subject,
      previewText: state.translatedFields.previewText ?? msg.previewText,
      isTranslated: true,
    };
  };

  // Le spunte sulle bolle dell'ospite: orologio in coda e in invio, ✓
  // inviato, ✓✓ chiare quando e' nella chat dello staff, ✓✓ azzurre quando
  // lo staff l'ha letto (readAt; isRead per i server che non mandano ancora readAt).
  const renderTicks = (msg: UiMessage) => {
    let icon: React.ReactNode;
    let label: string;
    if (msg.status === 'queued') {
      icon = <Clock className="h-3.5 w-3.5" strokeWidth={2.2} />;
      label = t('guestChat.ticks.queued');
    } else if (msg.status === 'sending') {
      icon = <Clock className="h-3.5 w-3.5" strokeWidth={2.2} />;
      label = t('guestChat.ticks.sending');
    } else if (msg.status === 'failed') {
      icon = <AlertCircle className="h-3.5 w-3.5 text-red-200" />;
      label = t('guestChat.notSent');
    } else if (msg.status === 'sent') {
      icon = <Check className="h-[15px] w-[15px]" strokeWidth={2.4} />;
      label = t('guestChat.ticks.sent');
    } else if (msg.readAt || msg.isRead === true) {
      icon = <CheckCheck className="h-[15px] w-[17px] text-cyan-300" strokeWidth={2.4} />;
      const at = timeLabel(msg.readAt);
      label = at ? t('guestChat.ticks.readAt', { time: at }) : t('guestChat.ticks.read');
    } else {
      icon = <CheckCheck className="h-[15px] w-[17px]" strokeWidth={2.4} />;
      label = t('guestChat.ticks.delivered');
    }
    return (
      <span role="img" aria-label={label} title={label} className="inline-flex items-center">
        {icon}
      </span>
    );
  };

  // L'ultimo messaggio dello staff a cui si puo' reagire: solo li' il tasto
  // "aggiungi reazione" (sotto ogni bolla era rumore).
  let lastStaffReactId: string | null = null;
  // L'ultima bolla dell'ospite (per "Letto alle …") e l'ultima in coda (per
  // "In attesa di rete").
  let lastGuestId: string | null = null;
  let lastQueuedId: string | null = null;
  for (let i = 0; i < shownMessages.length; i++) {
    const m = shownMessages[i];
    if (m.senderType === 'admin' && m.type === 'chat' && !isTempId(m.id) && !m.status) lastStaffReactId = m.id;
    if (m.senderType === 'guest') lastGuestId = m.id;
    if (m.status === 'queued') lastQueuedId = m.id;
  }

  const translationToggleClass =
    '-mb-3 -ml-1.5 -mt-2.5 inline-flex min-h-[44px] items-center gap-[5px] px-1.5 text-left text-[12px] font-semibold text-blue-700 hover:text-blue-800 focus:outline-none focus-visible:underline dark:text-blue-300 dark:hover:text-blue-200';
  const translatingNode = (
    <span className="inline-flex items-center gap-[5px] text-[12px] text-nva-muted dark:text-stone-400">
      <Loader2 className="h-3 w-3 motion-safe:animate-spin" strokeWidth={2.6} aria-hidden="true" />
      {t('guestChat.translating')}
    </span>
  );

  const renderMessage = (msg: UiMessage, prev: UiMessage | null, startsDay: boolean) => {
    const isGuest = msg.senderType === 'guest';
    const isEmail = msg.type === 'email';
    const isSystem = msg.senderType === 'system';
    const isDeliveryCard = (msg.kind === 'delivery' || msg.kind === 'delivered') && !!msg.meta;
    const isOrderCard = msg.kind === 'order' && !!msg.meta;
    const isCard = isDeliveryCard || isOrderCard;
    const isFailed = msg.status === 'failed';
    const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
    const hasPhotos = attachments.length > 0 && !isCard;
    // "📷" e' il testo dei messaggi di sole foto: si mostrano le foto e basta.
    const photoOnly = hasPhotos && isPhotoOnlyText(msg.message);
    const displayFields = getDisplayFields(msg);
    const tState = translationStates[msg.id];
    const showTranslationToggle = !isGuest && !!translationLang && !!tState?.translatedFields && translationChanged(tState.translatedFields, msg);
    // Card tradotte: la card resta com'e' (orario, articoli) e sotto compare
    // il testo del messaggio nella lingua scelta, con la nota se era a parte.
    // Una "traduzione" uguale all'originale (lingua gia' giusta, servizio
    // giu') non si mostra.
    const cardText = isCard && tState?.translatedFields
      && tState.translatedFields.message.trim() !== msg.message.trim()
      ? tState.translatedFields.message
      : '';
    const cardNote = isCard && tState?.translatedNote && tState.translatedNote.trim() !== cardNoteOf(msg)
      ? tState.translatedNote
      : '';
    const cardTranslated = !!(cardText || cardNote);
    // Reactions are only supported on chat messages between guest and admin
    // (server enforces this — emails and system messages return 404). Un
    // messaggio non ancora sul server non ha un id a cui reagire.
    const canReact = msg.type === 'chat' && !isSystem && !isTempId(msg.id) && !msg.status;
    // Il nome dello staff si ripete solo quando cambia chi scrive (o il
    // giorno): una fila di messaggi della stessa persona resta compatta.
    const showSender = !isGuest && (
      isEmail || startsDay || !prev || prev.senderType !== msg.senderType || prev.senderName !== msg.senderName
    );
    const time = timeLabel(msg.createdAt);

    const senderCaption = (className?: string) => (
      <span className={cn('flex min-w-0 items-center gap-[5px] text-[12px] font-bold leading-tight text-nva-muted dark:text-stone-400', className)}>
        {isEmail ? (
          <Mail className="h-3 w-3 flex-shrink-0" strokeWidth={2.4} aria-hidden="true" />
        ) : (
          <MessageCircle className="h-3 w-3 flex-shrink-0" strokeWidth={2.4} aria-hidden="true" />
        )}
        <span className="truncate">{msg.senderName || 'Nonna Vittoria'}</span>
      </span>
    );

    const reactionsRow = canReact ? (
      <MessageReactions
        reactions={msg.reactions}
        isOwnMessage={isGuest}
        canAdd={!isGuest && msg.id === lastStaffReactId}
        onReact={(emoji) => handleReact(msg.id, emoji)}
      />
    ) : null;

    // Ordine e consegna: card della famiglia, fuori dalla bolla, con chi
    // scrive sopra. La traduzione (se c'e') sta in fondo alla card.
    if (isCard) {
      const translation = translationLang && tState && (tState.isTranslating || cardTranslated) ? (
        <div className="flex flex-col items-start gap-1 border-t border-nva-hairline pt-2 dark:border-stone-800">
          {tState.isTranslating ? translatingNode : (
            <>
              {!tState.showOriginal && (
                <div className="flex flex-col gap-1 border-l-2 border-green-300 pl-2 dark:border-green-700">
                  {cardText && (
                    <p className="m-0 text-[14px] leading-snug text-nva-espresso dark:text-stone-100 chat-text">
                      <ChatLinkify text={cardText} tone="staff" />
                    </p>
                  )}
                  {cardNote && (
                    <p className="m-0 text-[13px] leading-snug text-nva-muted dark:text-stone-300 chat-text">
                      <span className="font-semibold">{t('guestChat.delivery.note')}:</span> {cardNote}
                    </p>
                  )}
                </div>
              )}
              <button type="button" onClick={() => toggleMessageTranslation(msg.id)} className={translationToggleClass}>
                <Globe className="h-[13px] w-[13px] flex-shrink-0" strokeWidth={2.2} aria-hidden="true" />
                {!tState.showOriginal
                  ? `${t('guestChat.translated')} · ${t('guestChat.showOriginal')}`
                  : `${t('guestChat.original')} · ${t('guestChat.showTranslation')}`}
              </button>
            </>
          )}
        </div>
      ) : null;
      return (
        <div className="flex flex-col items-start gap-1">
          {showSender && senderCaption('pl-1')}
          <div className="w-[88%] max-w-[360px]">
            {isDeliveryCard ? (
              <DeliveryCard meta={msg.meta!} kind={msg.kind === 'delivered' ? 'delivered' : 'delivery'} text={msg.message} time={time}>
                {translation}
              </DeliveryCard>
            ) : (
              <OrderCard meta={msg.meta!} time={time}>
                {translation}
              </OrderCard>
            )}
          </div>
          {reactionsRow}
        </div>
      );
    }

    // La riga della traduzione a sinistra, l'orario a destra (bolle dello staff).
    let translationNode: React.ReactNode = null;
    if (!isGuest && !photoOnly && translationLang) {
      if (tState?.isTranslating) {
        translationNode = translatingNode;
      } else if (showTranslationToggle) {
        translationNode = (
          <button type="button" onClick={() => toggleMessageTranslation(msg.id)} className={translationToggleClass}>
            <Globe className="h-[13px] w-[13px] flex-shrink-0" strokeWidth={2.2} aria-hidden="true" />
            {displayFields.isTranslated
              ? `${t('guestChat.translated')} · ${t('guestChat.showOriginal')}`
              : `${t('guestChat.original')} · ${t('guestChat.showTranslation')}`}
          </button>
        );
      } else if (tState?.error) {
        translationNode = (
          <span className="text-[12px] text-red-700 dark:text-red-400">{t('guestChat.translationError')}</span>
        );
      }
    }

    const isRead = !msg.status && (!!msg.readAt || msg.isRead === true);
    const readAtLabel = isGuest && msg.id === lastGuestId && isRead
      ? (timeLabel(msg.readAt) ? t('guestChat.ticks.readAt', { time: timeLabel(msg.readAt) }) : t('guestChat.ticks.read'))
      : '';

    return (
      <div className={cn('flex flex-col', isGuest ? 'items-end gap-[3px]' : 'items-start gap-0.5')}>
        <div
          className={cn(
            'min-w-0',
            photoOnly ? 'p-1.5' : isGuest ? 'px-3 pb-[7px] pt-[9px]' : 'px-3 pb-[7px] pt-2',
            isGuest
              ? cn('max-w-[82%] rounded-[18px_18px_6px_18px] bg-blue-600 text-white', isFailed && 'cursor-pointer opacity-80 ring-2 ring-red-400')
              : isEmail
              ? 'max-w-[85%] rounded-[18px_18px_18px_6px] border border-stone-200 bg-stone-100 dark:border-stone-700 dark:bg-stone-800'
              : 'max-w-[85%] rounded-[18px_18px_18px_6px] border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/30',
          )}
          onClick={isFailed ? () => retryMessage(msg) : undefined}
        >
          {showSender && senderCaption(cn('mb-[3px]', photoOnly && 'px-1.5 pt-0.5'))}

          {/* Subject for emails */}
          {isEmail && displayFields.subject && (
            <p className="m-0 mb-1 text-[12.5px] font-semibold text-nva-espresso dark:text-stone-200 chat-text">
              {displayFields.subject}
            </p>
          )}

          {/* Message content */}
          {photoOnly ? (
            <span className="sr-only">{t('guestChat.photo.only')}</span>
          ) : (
            <p className={cn('m-0 text-[15px] leading-[1.4] chat-text', isGuest ? 'text-white' : 'text-nva-espresso dark:text-stone-100')}>
              <ChatLinkify
                text={displayFields.previewText || displayFields.message}
                tone={isGuest ? 'guest' : 'staff'}
              />
            </p>
          )}

          {/* Foto (dell'ospite o dello staff) sotto il testo. Nella bolla non
              inviata il tocco riprova l'invio, quindi non aprono il visore. */}
          {hasPhotos && (
            <ChatPhotoGrid
              attachments={attachments}
              localThumbs={msg.localThumbs}
              tone={isGuest ? 'guest' : 'staff'}
              spaced={!photoOnly}
              onOpen={isFailed ? undefined : setViewerPhoto}
            />
          )}

          {/* Benvenuto all'ingresso: tre pulsanti che portano dritti a
              clima, lavatrice e WiFi. Il testo sopra dice cosa si può fare;
              i pulsanti lo fanno fare. */}
          {msg.kind === 'welcome' && (
            <div className="mt-2.5 grid grid-cols-3 gap-1.5">
              {[
                { key: 'climate', icon: <Snowflake />, label: t('myStay.climate.title'), to: '/my-stay?open=climate' },
                { key: 'laundry', icon: <WashingMachine />, label: t('myStay.laundry.title'), to: '/my-stay?open=laundry' },
                { key: 'wifi', icon: <Wifi />, label: t('myStay.wifi.title'), to: '/my-stay?open=wifi' },
              ].map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => { onClose(); navigate(a.to); }}
                  className="flex min-h-[54px] min-w-0 flex-col items-center justify-center gap-1 rounded-xl border border-green-200 bg-white px-1 py-1.5 text-center text-[12.5px] font-extrabold leading-tight text-green-800 transition-colors hover:bg-green-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-700 dark:border-green-800 dark:bg-stone-900 dark:text-green-200 [&>svg]:h-[17px] [&>svg]:w-[17px] [&>svg]:text-green-700 dark:[&>svg]:text-green-300"
                >
                  {a.icon}
                  <span className="max-w-full break-words">{a.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Ora (e spunte, sui propri messaggi) in basso a destra; la
              traduzione dello staff a sinistra, sulla stessa riga. */}
          {isGuest ? (
            <div className={cn(
              'mt-[3px] flex select-none items-center justify-end gap-1 text-[11px] leading-none tabular-nums text-blue-50',
              photoOnly && 'px-1.5 pb-0.5',
            )}>
              <span>{time}</span>
              {renderTicks(msg)}
            </div>
          ) : (
            <div className={cn('mt-[3px] flex items-center justify-between gap-2.5', photoOnly && 'px-1.5 pb-0.5')}>
              {translationNode}
              <span className="ml-auto select-none text-[11px] leading-tight tabular-nums text-nva-muted dark:text-stone-400">{time}</span>
            </div>
          )}
        </div>

        {readAtLabel && (
          <span className="pr-1 text-[11.5px] text-nva-muted dark:text-stone-400">{readAtLabel}</span>
        )}

        {msg.status === 'queued' && msg.id === lastQueuedId && !online && (
          <span className="inline-flex items-center gap-[5px] pr-1 text-[12px] font-semibold text-amber-800 dark:text-amber-300">
            <Clock className="h-[13px] w-[13px] flex-shrink-0 text-amber-700 dark:text-amber-300" strokeWidth={2.4} aria-hidden="true" />
            {t('guestChat.offline.queued')}
          </span>
        )}

        {isFailed && (
          <button
            type="button"
            onClick={() => retryMessage(msg)}
            className="-my-1 inline-flex min-h-[44px] items-center gap-1.5 px-0.5 text-[12.5px] font-bold text-red-700 focus:outline-none focus-visible:underline dark:text-red-400"
          >
            <RotateCw className="h-[15px] w-[15px]" strokeWidth={2.4} aria-hidden="true" />
            {t('guestChat.notSent')} · {t('guestChat.tapToRetry')}
          </button>
        )}

        {reactionsRow}
      </div>
    );
  };

  const renderMessageList = () => {
    const dayKeys = shownMessages.map((m) => romeDayKey(m.createdAt));
    return shownMessages.map((msg, i) => {
      const prev = i > 0 ? shownMessages[i - 1] : null;
      const startsDay = i === 0 || dayKeys[i] !== dayKeys[i - 1];
      const label = startsDay ? dayLabel(msg.createdAt, dayKeys[i]) : '';
      return (
        <React.Fragment key={msg.id}>
          {startsDay && label && (
            // Il giorno come gli occhielli della famiglia, fra due fili.
            <div className="mb-1 mt-2.5 flex items-center gap-2.5">
              <span aria-hidden="true" className="h-px flex-1 bg-[#E7DCCB] dark:bg-stone-800" />
              <span className="rounded-full bg-nva-cream px-2.5 py-[5px] text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-nva-muted dark:bg-stone-800 dark:text-stone-300">
                {label}
              </span>
              <span aria-hidden="true" className="h-px flex-1 bg-[#E7DCCB] dark:bg-stone-800" />
            </div>
          )}
          {renderMessage(msg, prev, startsDay)}
        </React.Fragment>
      );
    });
  };

  // Le risposte rapide si nascondono mentre si scrive, con le foto in
  // partenza, con la tastiera aperta (serve spazio) e a sessione scaduta.
  const showQuickReplies = !!token && !expired && !keyboardOpen && newMessage.length === 0 && pendingPhotos.length === 0;
  const cameraDisabled = !token || expired || !online || pendingPhotos.length >= MAX_PHOTOS_PER_MESSAGE;

  let listContent: React.ReactNode;
  if (expired) {
    listContent = <ChatSessionExpired onRelogin={handleRelogin} />;
  } else if (isLoading && shownMessages.length === 0) {
    listContent = <ChatLoadingSkeleton />;
  } else if (error && shownMessages.length === 0) {
    listContent = <ChatLoadError onRetry={() => loadMessages()} />;
  } else if (shownMessages.length === 0) {
    listContent = <ChatEmptyWelcome guestName={guestName} />;
  } else {
    listContent = (
      <>
        {guestName ? <ConnectedAs name={guestName} className="pb-1 pt-0.5" /> : null}
        {renderMessageList()}
      </>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        ref={contentRef}
        side="right"
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          try {
            contentRef.current?.focus({ preventScroll: true });
          } catch {
            contentRef.current?.focus();
          }
        }}
        className="flex w-full flex-col gap-0 overflow-hidden border-l-0 bg-nva-bone p-0 outline-none dark:bg-stone-950 sm:max-w-md sm:border-l sm:border-nva-edge dark:sm:border-stone-800"
        // Con la tastiera aperta la chat occupa solo la parte visibile dello
        // schermo: la testata in alto, la barra appena sopra la tastiera.
        style={viewport ? { top: viewport.top, height: viewport.height, bottom: 'auto' } : undefined}
        hideCloseButton
        onEscapeKeyDown={(e) => {
          // Esc con una foto aperta chiude la foto, con il menu delle
          // lingue chiude il menu: non tutta la chat.
          if (viewerPhoto) {
            e.preventDefault();
            setViewerPhoto(null);
          } else if (showLangDropdown) {
            e.preventDefault();
            setShowLangDropdown(false);
          }
        }}
      >
        <ChatHeader
          onBack={onClose}
          offline={!online}
          compact={keyboardOpen}
          safeTop={!viewport || viewport.top === 0}
          translationLang={translationLang}
          langMenuOpen={showLangDropdown}
          onToggleLangMenu={() => setShowLangDropdown(!showLangDropdown)}
          onPickLang={handleLanguageChange}
          langMenuRef={langDropdownRef}
          langMenuMaxHeight={viewport ? Math.max(160, viewport.height - 110) : undefined}
        />

        {!online && <ChatOfflineBanner />}

        {/* Non-refundable warning — shown only when isNonRefundable === true */}
        {isNonRefundable === true && !keyboardOpen && (
          <div className="flex-shrink-0 px-4 pt-3">
            <NonRefundableBadge isNonRefundable={isNonRefundable} variant="compact" />
          </div>
        )}

        {/* Messages area: fondo osso, la lista parte dal basso. */}
        <div className="relative min-h-0 flex-1">
          <div
            ref={setListEl}
            onScroll={handleListScroll}
            role="log"
            className="chat-list absolute inset-0 overflow-y-auto overflow-x-hidden overscroll-contain px-4 pb-[18px] pt-3.5"
          >
            <div className="flex min-h-full flex-col gap-2">
              {listContent}
            </div>
          </div>

          {showNewPill && (
            <button
              type="button"
              onClick={handleNewPillClick}
              className="absolute bottom-3 left-1/2 z-10 inline-flex min-h-[40px] -translate-x-1/2 items-center gap-1.5 rounded-full bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-lg hover:bg-blue-700"
            >
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
              {t('guestChat.newMessages')}
            </button>
          )}
        </div>

        {/* Error banner (i messaggi ci sono gia'). Senza rete lo dice gia'
            l'avviso ambra in alto. */}
        {error && online && !expired && shownMessages.length > 0 && (
          <div className="flex-shrink-0 border-t border-red-200 bg-red-50 px-4 py-2 dark:border-red-900/60 dark:bg-red-950/40">
            <p className="m-0 flex items-center gap-2 text-[13.5px] text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {error}
            </p>
          </div>
        )}

        {/* La barra in basso, come quella fissa dei servizi: risposte rapide,
            foto in partenza, casella da 16px (iOS non ingrandisce), foto e
            invio. In fondo lascia spazio alla barra di iOS (home indicator),
            non con la tastiera aperta (la copre lei). */}
        <div
          className="relative z-[2] flex flex-shrink-0 flex-col gap-2.5 border-t border-nva-edge bg-nva-card/[.97] px-4 pt-2.5 shadow-[0_-8px_24px_rgba(42,38,32,.08)] dark:border-stone-800 dark:bg-stone-900/95 dark:shadow-none"
          style={{ paddingBottom: keyboardOpen ? 10 : 'max(16px, calc(env(safe-area-inset-bottom, 0px) + 8px))' }}
        >
          {/* Risposte rapide: le richieste piu' comuni con un tocco. Blu i
              servizi, ambra le proposte del negozio. */}
          {showQuickReplies && (
            <div
              className="scrollbar-hide -mx-4 flex gap-2 overflow-x-auto px-4"
              style={{ WebkitMaskImage: 'linear-gradient(90deg, #000 88%, transparent)', maskImage: 'linear-gradient(90deg, #000 88%, transparent)' }}
              role="group"
              aria-label={t('guestChat.quick.label')}
            >
              {quickReplies.map((q) => (
                <button
                  key={q.key}
                  type="button"
                  onClick={q.onClick}
                  title={q.offer ? q.label : undefined}
                  className={cn(
                    'flex min-h-[44px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border pl-[11px] pr-[13px] text-[14px] font-semibold transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:flex-shrink-0',
                    q.offer
                      ? 'max-w-[15rem] border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 [&>svg]:text-amber-700 dark:[&>svg]:text-amber-300'
                      : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:bg-stone-800 dark:text-blue-300 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-300',
                  )}
                >
                  {q.icon}
                  <span className="min-w-0 truncate">{q.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* L'avviso di una risposta rapida (es. late check-out non ancora
              disponibile): informativo, se ne va da solo. */}
          {quickNotice && (
            <div
              role="status"
              className="flex items-start gap-2 rounded-2xl border border-blue-200 bg-blue-50 py-2 pl-3 pr-1 text-[13.5px] leading-snug text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-100"
            >
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-700 dark:text-blue-300" aria-hidden="true" />
              <span className="min-w-0 flex-1 py-px">{quickNotice}</span>
              <button
                type="button"
                onClick={() => setQuickNotice(null)}
                className="-my-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:text-blue-300"
                aria-label={t('common.close')}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Foto in partenza: compresse e caricate una alla volta, poi
              spedite con il pulsante di invio insieme al testo (se c'e'). */}
          <PendingPhotoStrip photos={pendingPhotos} onRetry={retryPhoto} onRemove={removePhoto} />
          {photoNotice && (
            <p role="alert" className="m-0 flex items-center gap-1.5 text-[12.5px] text-red-700 dark:text-red-400">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span className="min-w-0">{photoNotice}</span>
            </p>
          )}

          <div className="flex items-end gap-1">
            {/* 16px (text-base): sotto questa misura iOS ingrandisce la
                pagina quando la casella prende il focus. Il tasto Invio
                dice "invia" solo con mouse e tastiera, dove spedisce: sul
                telefono va a capo. A sessione scaduta si legge e basta. */}
            <textarea
              ref={setTextareaEl}
              rows={1}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder={expired ? t('guestChat.expired.placeholder') : t('guestChat.placeholder')}
              aria-label={expired ? t('guestChat.expired.placeholder') : t('guestChat.placeholder')}
              readOnly={expired}
              aria-disabled={expired || undefined}
              enterKeyHint={hasFinePointer() ? 'send' : 'enter'}
              maxLength={5000}
              className={cn(
                'min-h-[48px] min-w-0 flex-1 resize-none overflow-y-hidden rounded-[24px] border-[1.5px] px-4 py-[10.5px] text-base leading-6 outline-none',
                'placeholder:text-stone-500 dark:placeholder:text-stone-400',
                expired
                  ? 'cursor-default border-nva-foot bg-[#F4EFE6] text-stone-600 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-400'
                  : 'border-[#9A9185] bg-white text-nva-espresso focus:border-blue-600 focus:ring-[3px] focus:ring-blue-600/20 dark:border-stone-600 dark:bg-stone-950 dark:text-stone-100 dark:focus:border-blue-400 dark:focus:ring-blue-400/30',
              )}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFilesPicked}
              className="hidden"
              tabIndex={-1}
              aria-hidden="true"
            />
            {/* Senza rete le foto non partono: il tasto resta spento finché torna. */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={cameraDisabled}
              className="flex h-12 w-11 flex-shrink-0 items-center justify-center rounded-full text-stone-600 transition-colors hover:bg-stone-200/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 disabled:opacity-35 disabled:hover:bg-transparent dark:text-stone-400 dark:hover:bg-stone-800"
              aria-label={t('guestChat.photo.add')}
              title={t('guestChat.photo.add')}
            >
              <Camera className="h-[22px] w-[22px]" strokeWidth={2} aria-hidden="true" />
            </button>
            {/* onMouseDown: il tocco sul pulsante non ruba il focus alla
                casella, cosi' la tastiera resta aperta dopo l'invio. */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSend}
              disabled={!canSend}
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-[0_6px_14px_rgba(37,99,235,.3)] transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:opacity-40 disabled:shadow-none disabled:hover:bg-blue-600 dark:focus-visible:ring-offset-stone-900"
              aria-label={t('guestChat.send')}
            >
              {isSending || photosBusy ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Dentro il contenuto del drawer: fuori sarebbe un tocco "esterno". */}
        <ChatPhotoViewer photo={viewerPhoto} onClose={closeViewer} />

        {/* "Accedi di nuovo" senza un login del genitore (Il mio soggiorno). */}
        {reloginOpen && expired && <ChatRelogin onClose={() => setReloginOpen(false)} />}
      </SheetContent>
    </Sheet>
  );
};

/** L'icona di una proposta del negozio (al posto dell'emoji di prima). */
function offerIcon(serviceId: string): React.ReactNode {
  switch (serviceId) {
    case 'late_checkout': return <Clock />;
    case 'caffe_brioche': return <Coffee />;
    case 'welcome_wine': return <Wine />;
    case 'water_bottles': return <GlassWater />;
    case 'extra_cleaning': return <Sparkles />;
    case 'linen_supplies': return <Bath />;
    case 'paper_supplies': return <Toilet />;
    case 'hygiene_supplies': return <ShowerHead />;
    default: return <Sparkles />;
  }
}

export default GuestChatDrawer;
